import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { CustomerProfileModal } from "../../shared/CustomerProfileModal";
import { fetchCustomerAddressesList, fetchCustomersList } from "../../shared/customerMastersApi";
import { IconCart, IconEye, IconOrders, IconPlus, IconWallet, IconWishlist } from "../../shared/toolbarIcons";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function pickDisplayAddress(addresses, customerId) {
  const cid = Number(customerId);
  const list = addresses.filter((a) => Number(a.customerId) === cid);
  if (list.length === 0) return null;
  const def = list.find((a) => a.isDefault);
  return def ?? list[0];
}

function formatLocation(addr, countries, states, cities) {
  if (!addr) return "-";
  const country = countries.find((c) => c.id === addr.countryId);
  const state = states.find((s) => s.id === addr.stateId);
  const city = cities.find((ci) => ci.id === addr.cityId);
  return [city?.name, state?.name, country?.name].filter(Boolean).join(", ") || "-";
}

function truncateText(text, max = 36) {
  if (text == null || text === "") return "-";
  const s = String(text);
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

function formatWalletBalanceByCustomer(walletRows) {
  const m = new Map();
  for (const w of walletRows) {
    const cid = Number(w.customerId ?? w.CustomerId);
    if (!Number.isFinite(cid)) continue;
    m.set(cid, (m.get(cid) ?? 0) + Number(w.balance ?? w.Balance ?? 0));
  }
  return m;
}

function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function CustomersList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const balanceByCustomer = useMemo(() => formatWalletBalanceByCustomer(wallets), [wallets]);

  const rows = useMemo(() => {
    return customers.map((c) => {
      const addr = pickDisplayAddress(addresses, c.id);
      const addressLine = addr?.address ?? "-";
      const location = formatLocation(addr, countries, states, cities);
      const walletBalance = balanceByCustomer.has(Number(c.id)) ? balanceByCustomer.get(Number(c.id)) : null;

      return {
        id: c.id,
        customer: c,
        address: addr,
        addressLine,
        location,
        walletBalance
      };
    });
  }, [customers, addresses, countries, states, cities, balanceByCustomer]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [cRes, aRes, countryRes, stateRes, cityRes, wRes] = await Promise.all([
      fetchCustomersList(),
      fetchCustomerAddressesList(),
      apiGet("/api/Countries"),
      apiGet("/api/States"),
      apiGet("/api/Cities"),
      apiGet("/api/Wallets")
    ]);
    if (!cRes.ok) {
      setError(cRes.error ?? "Failed to load customers");
      setCustomers([]);
    } else {
      setCustomers(cRes.items);
    }
    if (!aRes.ok) {
      setError((prev) => prev ?? aRes.error ?? "Failed to load customer addresses");
      setAddresses([]);
    } else {
      setAddresses(aRes.items);
    }
    if (countryRes.ok) setCountries(countryRes.data ?? []);
    if (stateRes.ok) setStates(stateRes.data ?? []);
    if (cityRes.ok) setCities(cityRes.data ?? []);
    if (!wRes.ok) {
      setWallets([]);
    } else {
      setWallets(wRes.data ?? []);
    }
    setLoading(false);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.85,
        minWidth: 110,
        valueGetter: (_v, row) => row.customer.name ?? "-"
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 140,
        valueGetter: (_v, row) => row.customer.email ?? "-"
      },
      {
        field: "phoneNo",
        headerName: "Phone",
        minWidth: 110,
        valueGetter: (_v, row) => row.customer.phoneNo ?? "-"
      },
      {
        field: "addressLine",
        headerName: "Address",
        flex: 0.95,
        minWidth: 120,
        renderCell: (params) => {
          const raw = params.row.addressLine ?? "-";
          const short = truncateText(raw, 32);
          return (
            <span title={raw !== "-" ? raw : undefined} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {short}
            </span>
          );
        }
      },
      {
        field: "location",
        headerName: "Location",
        flex: 0.85,
        minWidth: 110,
        renderCell: (params) => {
          const raw = params.row.location ?? "-";
          const short = truncateText(raw, 28);
          return (
            <span title={raw !== "-" ? raw : undefined} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {short}
            </span>
          );
        }
      },
      {
        field: "balance",
        headerName: "Balance",
        minWidth: 100,
        valueGetter: (_v, row) => {
          const w = row.walletBalance;
          return w == null ? "—" : formatMoney(w);
        }
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 260,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const c = params.row.customer;
          const onWalletTransactions = () => navigate(`/wallet-transactions?customer=${c.id}`);
          const onCustomerWishlists = () => navigate(`/customer-wishlists?customer=${c.id}`);
          const onCustomerCarts = () => navigate(`/customer-carts?customer=${c.id}`);
          const onCustomerOrders = () => navigate(`/customer-orders?customer=${c.id}`);
          const onEdit = () => navigate(`/customers?edit=${c.id}`);

          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="View" onClick={() => setViewRow(params.row)}>
                <IconEye />
              </button>
              <button className="icon-button" type="button" title="Edit" onClick={onEdit}>
                ✏️
              </button>
              <button
                className="icon-button"
                type="button"
                title="Orders"
                aria-label="Customer orders"
                onClick={onCustomerOrders}
              >
                <IconOrders />
              </button>
              <button
                className="icon-button"
                type="button"
                title="Wallet transactions"
                aria-label="Wallet transactions"
                onClick={onWalletTransactions}
              >
                <IconWallet />
              </button>
              <button
                className="icon-button"
                type="button"
                title="Wishlists"
                aria-label="Customer wishlists"
                onClick={onCustomerWishlists}
              >
                <IconWishlist />
              </button>
              <button className="icon-button" type="button" title="Cart" aria-label="Customer cart" onClick={onCustomerCarts}>
                <IconCart />
              </button>
            </div>
          );
        }
      }
    ],
    [navigate]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Customers</h2>
          <p>{loading ? "Loading customers..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/customers" className="icon-button icon-button--toolbar" title="Add customer" aria-label="Add customer">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={{
              border: "none",
              fontFamily: "inherit",
              fontSize: "0.9rem",
              height: "100%",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f2fcf5",
                borderBottom: "1px solid #e8ecea",
                fontWeight: 600
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e8ecea",
                backgroundColor: "#fbfffc"
              },
              "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
                backgroundColor: "#fcfffd"
              },
              "& .MuiDataGrid-cell": {
                borderBottomColor: "#e8ecea"
              }
            }}
          />
        </div>
      </div>
      <CustomerProfileModal row={viewRow} onClose={() => setViewRow(null)} />
    </div>
  );
}
